import axios from 'axios';

import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import useApi from '@/hooks/useApi/useApi';
import { queryKeys } from '@/lib/queryKeys';
import { Project } from '@/types/types';

type UseProjectOptions = {
  redirectOn403?: boolean;
  enabled?: boolean;
};

type ProjectForbiddenReason = 'cross_company' | 'not_project_manager' | string;

const getForbiddenReason = (error: unknown): ProjectForbiddenReason | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return null;
  return (error.response?.data as { reason?: string } | undefined)?.reason ?? null;
};

const useProject = (
  projectId: string | number,
  { redirectOn403 = false, enabled = true }: UseProjectOptions = {}
) => {
  const { axiosInstance } = useApi();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: queryKeys.projects.detail(projectId).queryKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Project>(`/api/v1/projects/${projectId}`);
      return data;
    },
    enabled: !!projectId && enabled,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) return false;
      return failureCount < 3;
    },
  });

  const forbiddenReason = query.isError ? getForbiddenReason(query.error) : null;

  useEffect(() => {
    // Only a cross-company (partner-shared) project has a fallback view to
    // redirect to — any other reason (e.g. a planner who doesn't manage this
    // project) has no "other place" that would show it, so stay put and let
    // the caller render a forbidden state instead.
    if (redirectOn403 && forbiddenReason === 'cross_company' && projectId) {
      navigate({
        to: '/app/partner-projects/$projectId',
        params: { projectId: String(projectId) },
        replace: true,
      });
    }
  }, [redirectOn403, forbiddenReason, projectId, navigate]);

  return { ...query, forbiddenReason };
};

export default useProject;
